import { APP_PLATFORM_LIST, APP_PLATFORM_TYPES, isMobileApp, isExtensionApp } from '@/utils/apps';
import { getActiveApplicationsMap } from '@/data/session/session-globals';
import { App } from '@/data/session/types';

interface TrackEventDataParams {
    pageName: string;
    totalSelectedApps: number;
    isPlatformApp: string;
    webApps: number;
    mobileApps: number;
    iosApps: number;
    androidApps: number;
    selectedAppTypes: string;
    extensionApps: number;
    integratedApps: number;
    nonIntegratedApps: number;
}

export function trackMultiAppSelectionEvents (selectedApps?: App[] | null, vuePageName?: string) {
    if (!selectedApps?.length) return;

    // other places that uses multi app selector with slight different implementation might not provide all the apps info, so we need to get the full app info
    const appMap = getActiveApplicationsMap();
    const selectedAppsWithFullInfo = selectedApps.map((app) => {
        const fullAppInfo = appMap[app.id];
        if (fullAppInfo) {
            return fullAppInfo;
        }

        return app;
    });

    const trackEventData: TrackEventDataParams = {
        pageName: getPageName(vuePageName),
        totalSelectedApps: selectedAppsWithFullInfo.length,
        isPlatformApp: selectedAppsWithFullInfo.filter((app) =>
            APP_PLATFORM_LIST.find((platform) => platform === app.id),
        ).length
            ? 'yes'
            : 'no',
        webApps: selectedAppsWithFullInfo.filter((app) => app.platform === APP_PLATFORM_TYPES.WEB).length,
        mobileApps: selectedAppsWithFullInfo.filter((app) => isMobileApp(app.platform)).length,
        iosApps: selectedAppsWithFullInfo.filter((app) => app.platform === APP_PLATFORM_TYPES.IOS).length,
        androidApps: selectedAppsWithFullInfo.filter((app) => app.platform === APP_PLATFORM_TYPES.ANDROID).length,
        selectedAppTypes: getSelectedAppTypes(selectedAppsWithFullInfo),
        extensionApps: selectedAppsWithFullInfo.filter((app) => isExtensionApp(app.platform)).length,
        integratedApps: selectedAppsWithFullInfo.filter((app) => app.integrated === true).length,
        nonIntegratedApps: selectedAppsWithFullInfo.filter((app) => app.integrated === false).length,
    };
    window?.pendo?.track('Application Filter', trackEventData);
}

function getSelectedAppTypes (selectedAppsWithFullInfo: App[]): string {
    const selectedAppTypes = selectedAppsWithFullInfo.reduce((acc, app) => {
        if (APP_PLATFORM_LIST.find((platform) => platform === app.id)) {
            acc.add(app.displayName);
        }
        if (app.platform === APP_PLATFORM_TYPES.WEB) {
            acc.add('Web');
        }
        if (isMobileApp(app.platform)) {
            acc.add('Mobile');
        }
        if (isExtensionApp(app.platform)) {
            acc.add('Extension');
        }

        return acc;
    }, new Set());

    return [...selectedAppTypes].sort().join(', ');
}

function getPageName (vuePageName?: string): string {
    // Use Vue page name if available
    if (vuePageName) {
        return vuePageName;
    }

    // Otherwise, use Angular page name base on pathname
    // Order of Angular page list matters since visitor matches both visitors and visitor for visitor list and visitor details routes respectively.
    // So we need to check from more specific to less specific.
    const angularPageRouteNames: string[] = [
        'visitors',
        'accounts',
        'visitor',
        'account',
        'parentAccount',
        'net-promoter-system',
    ];
    const angularPageRouteName: string | undefined = angularPageRouteNames.find((pageName) =>
        window.location?.pathname.includes(pageName),
    );

    switch (angularPageRouteName) {
        case 'visitors':
            return 'Visitor List';
        case 'visitor':
            return 'Visitor Details';
        case 'accounts':
            return 'Account List';
        case 'account':
        case 'parentAccount':
            return 'Account Details';
        case 'net-promoter-system':
            return 'NPS List';
        default:
            return 'Unknown';
    }
}
